import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Typography, Alert, Empty, Tag, Space, Input, Button } from 'antd';
import { TeamOutlined, SearchOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { fiscalAdminService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import FichaFiscal from './FichaFiscal';

const { Title, Text } = Typography;

/**
 * Cartera del fiscal-admin: punto de entrada único.
 *
 * Lista los contribuyentes que alcanza, permite localizarlos por RFC o nombre y
 * abre la gestión fiscal sobre la empresa elegida en la misma pantalla. No hay
 * sección de gestión aparte: se entra siempre por el cliente.
 *
 * El alcance se deriva de los planes asignados, así que es dinámico: un cliente
 * que cambia a un plan fuera de la cartera simplemente deja de aparecer.
 */
const Cartera = () => {
  const { isAdmin } = useAuth();

  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('');
  const [seleccion, setSeleccion] = useState(null);

  useEffect(() => {
    fetchCartera();
  }, []);

  const fetchCartera = async () => {
    try {
      const data = await fiscalAdminService.getMiCartera();
      setClientes(data?.clientes || []);
    } catch (err) {
      setError('Error al cargar la cartera');
      console.error('Error fetching cartera:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Una fila por EMPRESA, no por cliente: el RFC vive en la empresa y un
   * cliente puede tener varias. Aplanarlo evita que la tabla tenga que elegir
   * cuál RFC mostrar.
   */
  const filas = useMemo(() => {
    const salida = [];

    clientes.forEach((cliente) => {
      if (!cliente.empresas || cliente.empresas.length === 0) {
        salida.push({
          key: cliente.id,
          clienteId: cliente.id,
          cliente: cliente.name,
          email: cliente.email,
          plan: cliente.plan_name,
          companyId: null,
          rfc: null,
          razonSocial: null,
        });
        return;
      }

      cliente.empresas.forEach((empresa) => {
        salida.push({
          key: `${cliente.id}:${empresa.id}`,
          clienteId: cliente.id,
          cliente: cliente.name,
          email: cliente.email,
          plan: cliente.plan_name,
          companyId: empresa.id,
          rfc: empresa.tax_id,
          razonSocial: empresa.tax_name,
        });
      });
    });

    return salida;
  }, [clientes]);

  const filasVisibles = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return filas;

    return filas.filter((f) =>
      [f.cliente, f.email, f.rfc, f.razonSocial]
        .filter(Boolean)
        .some((campo) => String(campo).toLowerCase().includes(q))
    );
  }, [filas, filtro]);

  const columns = [
    {
      title: 'Contribuyente',
      dataIndex: 'cliente',
      key: 'cliente',
      render: (nombre, fila) => (
        <Space direction="vertical" size={0}>
          <Text strong>{nombre}</Text>
          {fila.razonSocial && fila.razonSocial !== nombre && (
            <Text type="secondary" style={{ fontSize: 12 }}>{fila.razonSocial}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'RFC',
      dataIndex: 'rfc',
      key: 'rfc',
      render: (rfc) => (rfc ? <Tag color="geekblue">{rfc}</Tag> : <Tag>Sin empresa</Tag>),
    },
    { title: 'Correo', dataIndex: 'email', key: 'email' },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan) => (plan ? <Tag color="blue">{plan}</Tag> : <Tag>Sin plan</Tag>),
    },
    {
      title: '',
      key: 'accion',
      render: (_, fila) =>
        fila.companyId ? (
          <Button type="primary" size="small" onClick={() => setSeleccion(fila)}>
            Gestionar
          </Button>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>Sin empresa registrada</Text>
        ),
    },
  ];

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  // Gestión de la empresa elegida, sobre la misma pantalla.
  if (seleccion) {
    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => setSeleccion(null)}>
            Volver a la cartera
          </Button>
          <Text strong>{seleccion.cliente}</Text>
          <Tag color="geekblue">{seleccion.rfc}</Tag>
        </Space>

        <FichaFiscal companyId={seleccion.companyId} />
      </Space>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            <TeamOutlined /> Mi cartera
          </Title>
          <Text type="secondary">
            {isAdmin
              ? 'Como Admin ves todos los contribuyentes, sin restricción de cartera.'
              : 'Contribuyentes de los planes asignados a tu perfil. Si un cliente cambia de plan, deja de aparecer aquí.'}
          </Text>
        </div>

        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar por RFC, nombre o correo"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          style={{ maxWidth: 420 }}
        />

        <Table
          rowKey="key"
          loading={loading}
          dataSource={filasVisibles}
          columns={columns}
          pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
          scroll={{ x: true }}
          locale={{
            emptyText: (
              <Empty
                description={
                  filtro
                    ? `Sin coincidencias para "${filtro}" en tu cartera`
                    : 'Tu cartera está vacía. Un Admin debe asignarte al menos un plan.'
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Space>
    </Card>
  );
};

export default Cartera;
